"use client"

import { useState, useEffect, useMemo } from "react"
import { Box, Flex, HStack, Stack, Text, Portal, createListCollection, Select, useBreakpointValue } from "@chakra-ui/react"
import Image from "next/image"
import { beautifyPrice } from "@/utils/price"
import { getImageSrc } from "@/utils/file"
import { Button } from "@/components/ui/button"
import { LookbookOutfit } from "@/types/lookbook"
import { Product } from "@/types/product"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { AddToCartDto } from "@/types/cart"
import { addToCartRequest } from "@/services/cart"
import { openDrawer } from "@/shared/store/drawer"
import {sortSizes} from "@/utils/sizes";
import { useTranslations } from "next-intl";

interface OutfitSelectorProps {
  outfit: LookbookOutfit
  onAddOutfitToCart?: (items: Array<{ productId: Product['id']; sizeName: string | null }>) => void
  isLoading?: boolean
}

export function OutfitSelector({ outfit, onAddOutfitToCart, isLoading }: OutfitSelectorProps) {
  const t = useTranslations();
  const [selectedSizes, setSelectedSizes] = useState<Record<string, string | null>>({})

  const queryClient = useQueryClient();
  const isMobile = useBreakpointValue({ base: true, sm: true, md: false });

  const totalPrice = useMemo(() => {
    return outfit.items.reduce((sum, item) => sum + item.product.price, 0)
  }, [outfit.items])

  // Проверяем, есть ли у товара реальные размеры
  const hasSizes = (product: Product) => {
    if (!product.stocks || product.stocks.length === 0) return false
    const validStocks = product.stocks.filter((s) => s.amount > 0)
    if (validStocks.length === 0) return false
    return validStocks.some((s) => s.sizeName !== null)
  }

  // При монтировании сразу помечаем товары без размера как “выбранные”
  useEffect(() => {
    const preselected: Record<string, string | null> = {}
    outfit.items.forEach((item) => {
      if (!hasSizes(item.product)) {
        preselected[item.product.id] = null
      }
    })
    setSelectedSizes(preselected)
  }, [outfit])

  const handleSizeChange = (productId: Product["id"], value: string) => {
    setSelectedSizes((prev) => ({ ...prev, [productId]: value }))
  }

  const allSelected = useMemo(() => {
    return outfit.items.every((item) => {
      const product = item.product
      if (!hasSizes(product)) return true
      return Boolean(selectedSizes[product.id])
    })
  }, [outfit.items, selectedSizes])

  const addToCartMutation = useMutation({
    mutationFn: async (dto: AddToCartDto) => {
      await addToCartRequest(dto);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cart"] });
      openDrawer({ type: "main", tab: "cart" });
    },
  });

  const handleAddOutfit = () => {
    if (!allSelected) {
      alert(t('choose_size_for_products_outfit'))
      return
    }

    const selectedItems = outfit.items.map((item) => ({
      productId: item.product.id,
      sizeName: selectedSizes[item.product.id] ?? null,
    }))

    Promise.all(selectedItems.map(item => {
      const dto: AddToCartDto = {
        amount: 1,
        productId: item.productId,
        sizeName: item.sizeName,
      };
      return addToCartMutation.mutate(dto);
    })).then(() => {
      onAddOutfitToCart?.(selectedItems)
    });
  }

  return isMobile ? (
    // Мобильная версия
    <Stack
      align={"start"}
      gap={{ base: "16px", md: "40px" }}
    >
      {/* Заголовок */}
      <Stack
        direction="row"
        align="flex-end"
        justify={{ base: "flex-start", md: "space-between" }}
        gap={3}
        w="100%"
      >
        <Stack gap={1} flex={1}>
          {outfit.isNew && <Text textStyle={"bold16"} color={"brand"}>// NEW</Text>}
          <Text textStyle={"bold24"}>{outfit.name}</Text>
        </Stack>
        <Text textStyle={"bold24"}>{beautifyPrice(totalPrice, true)}</Text>
      </Stack>

      {/* Левая часть: фото */}
      <Box w={{ base: "100%", md: "50%" }}>
        <Stack direction="row" gap={0}>
          <Box flex={1} position={'relative'} aspectRatio={'340/508'}>
            <Image
              src={getImageSrc(outfit)}
              alt={outfit.images?.[0]?.type || outfit.name}
              fill
              style={{
                objectFit: "cover",
              }}
            />
          </Box>
          <Box flex={1} position={'relative'} aspectRatio={'340/508'}>
            <Image
              src={getImageSrc(outfit, "gallery")}
              alt={outfit.images?.[0]?.type || outfit.name}
              fill
              style={{
                objectFit: "cover",
              }}
            />
          </Box>
        </Stack>
      </Box>

      {/* Правая часть */}
      <Stack
        w={{ base: "100%", md: "50%" }}
        px={{ base: 0, md: 2.5 }}
        py={{ base: 0, md: 10 }}
        alignItems={"flex-start"}
        gap={6}
      >
        {/* Список товаров */}
        <HStack alignItems={"flex-start"} gap={"16px"} w={"100%"} overflowX={"auto"}>
          {outfit.items.map((item) => {
            const product = item.product
            const allStocks = product.stocks ?? []
            const sizeStocks = allStocks.filter((s) => s.sizeName !== null)
            const isSizeAvailable = sizeStocks.length > 0
            const collection = createListCollection({
              items: sortSizes(sizeStocks)?.map((s) => ({
                label: s.sizeName!,
                value: s.sizeName!,
                disabled: s.amount <= 0,
              })),
            })

            return (
              <Stack key={item.id} w={"154px"} align={"flex-start"} gap={3} justify="space-between">
                <Stack align={"flex-start"} gap={"19px"}>
                  {/* ФОТО ПРЕДМЕТА */}
                  <Box minW="92px" maxW="154px" w="154px" h="92px" position="relative">
                    <Image
                      src={getImageSrc(product)}
                      alt={product.name}
                      fill
                      style={{ objectFit: "contain" }}
                    />
                  </Box>
                  {/* НАЗВАНИЕ И ЦЕНА */}
                  <Box flex="1">
                    <Text textStyle="bold14">{product.name}</Text>
                    <HStack gap={2}>
                      <Text textStyle="bold12">{beautifyPrice(product.price)}</Text>
                      <Text textStyle={"bold12"} color={"text.secondary"}>
                        x1
                      </Text>
                    </HStack>
                  </Box>
                </Stack>

                {isSizeAvailable ? (
                  <Select.Root
                    collection={collection}
                    value={selectedSizes[product.id] ? [selectedSizes[product.id]!] : []}
                    onValueChange={(e) => handleSizeChange(product.id, e.value[0])}
                  >
                    <Select.HiddenSelect />
                    <Select.Control>
                      <Select.Trigger justifyContent={{ _placeholderShown: "center" }}
                        css={{
                          "&[data-placeholder-shown] > *": {
                            color: "text.primary",
                            textTransform: "lowercase",
                          },
                        }}
                        borderRadius={"none"} borderColor={"stroke"}>
                        <Select.ValueText textStyle={"bold14"} textTransform={"uppercase"} maxWidth={"100%"} placeholder={t('select_size')} />
                      </Select.Trigger>
                    </Select.Control>
                    <Portal>
                      <Select.Positioner>
                        <Select.Content>
                          {collection.items.map((s) => (
                            <Select.Item key={s.value} item={s}>
                              {s.label}
                              <Select.ItemIndicator />
                            </Select.Item>
                          ))}
                        </Select.Content>
                      </Select.Positioner>
                    </Portal>
                  </Select.Root>
                ) : null}
              </Stack>
            )
          })}
        </HStack>

        {/* Кнопки */}
        <HStack w={"100%"}>
          <Button
            w={"full"}
            size="lg"
            height={"56px"}
            borderRadius={"none"}
            onClick={handleAddOutfit}
            disabled={isLoading || !allSelected}
            loading={isLoading}
          >
            <Text textStyle={"bold16"}>
              {t('add_to_cart')}
            </Text>
          </Button>
        </HStack>
      </Stack>
    </Stack>
  ) :
    (
      // Десктопная версия
      <Flex
        direction={{ base: "column", md: "row" }}
        align={"start"}
        gap={{ base: "16px", md: "40px" }}
      >
        {/* Левая часть: фото */}
        <Box w={{ base: "100%", md: "50%" }}>
          <Stack direction="row" gap={0}>
            <Box flex={1} position={'relative'} aspectRatio={'340/508'}>
              <Image
                src={getImageSrc(outfit)}
                alt={outfit.images?.[0]?.type || outfit.name}
                fill
                style={{
                  objectFit: "cover",
                }}
              />
            </Box>
            <Box flex={1} position={'relative'} aspectRatio={'340/508'}>
              <Image
                src={getImageSrc(outfit, "gallery")}
                alt={outfit.images?.[0]?.type || outfit.name}
                fill
                style={{
                  objectFit: "cover",
                }}
              />
            </Box>
          </Stack>
        </Box>

        {/* Правая часть */}
        <Flex
          w={{ base: "100%", md: "50%" }}
          px={{ base: 0, md: 2.5 }}
          py={{ base: 0, md: 10 }}
          direction={"column"}
          alignItems={"flex-start"}
          gap={6}
        >
          {/* Заголовок */}
          <Stack
            direction="row"
            align="flex-end"
            justify={{ base: "flex-start", md: "space-between" }}
            gap={3}
            w="100%"
          >
            <Stack gap={1} flex={1}>
              {outfit.isNew && <Text textStyle={"bold16"} color={"brand"}>// NEW</Text>}
              <Text textStyle={"bold24"}>{outfit.name}</Text>
            </Stack>
            <Text textStyle={"bold24"}>{beautifyPrice(totalPrice, true)}</Text>
          </Stack>

          {/* Список товаров */}
          <Stack gap={6} w={"100%"}>
            {outfit.items.map((item) => {
              const product = item.product
              const allStocks = product.stocks ?? []
              const sizeStocks = allStocks.filter((s) => s.sizeName !== null)
              const isSizeAvailable = sizeStocks.length > 0
              const collection = createListCollection({
                items: sizeStocks.map((s) => ({
                  label: s.sizeName!,
                  value: s.sizeName!,
                  disabled: s.amount <= 0,
                })),
              })

              return (
                <HStack key={item.id} align={"flex-start"} gap={3} justify="space-between">
                  <Flex direction={"row"} align={"flex-start"} gap={3}>
                    {/* ФОТО ПРЕДМЕТА */}
                    <Box minW="74px" maxW="92px">
                      <Image
                        src={getImageSrc(product)}
                        alt={product.name}
                        width="74"
                        height="92"
                        objectFit="cover"
                      />
                    </Box>
                    {/* НАЗВАНИЕ И ЦЕНА */}
                    <Box flex="1">
                      <Text textStyle="bold14">{product.name}</Text>
                      <HStack gap={2}>
                        <Text textStyle="bold12">{beautifyPrice(product.price)}</Text>
                        <Text textStyle={"bold12"} color={"text.secondary"}>
                          x1
                        </Text>
                      </HStack>
                    </Box>
                  </Flex>

                  {isSizeAvailable ? (
                    <Select.Root
                      width="230px"
                      textStyle={"bold14"}
                      collection={collection}
                      value={selectedSizes[product.id] ? [selectedSizes[product.id]!] : []}
                      onValueChange={(e) => handleSizeChange(product.id, e.value[0])}
                    >
                      <Select.HiddenSelect />
                      <Select.Control>
                        <Select.Trigger justifyContent={{ _placeholderShown: "center" }}
                          css={{
                            "&[data-placeholder-shown] > *": {
                              color: "text.primary",
                              textTransform: "lowercase",
                            },
                          }}
                          borderRadius={"none"} borderColor={"stroke"}>
                          <Select.ValueText textStyle={"bold14"} textTransform={"uppercase"} maxWidth={"100%"} placeholder={t('select_size')} />
                        </Select.Trigger>
                      </Select.Control>
                      <Portal>
                        <Select.Positioner>
                          <Select.Content>
                            {collection.items.map((s) => (
                              <Select.Item key={s.value} item={s}>
                                {s.label}
                                <Select.ItemIndicator />
                              </Select.Item>
                            ))}
                          </Select.Content>
                        </Select.Positioner>
                      </Portal>
                    </Select.Root>
                  ) : null}
                </HStack>
              )
            })}
          </Stack>

          {/* Кнопка */}
          <Button
            w={"full"}
            size="lg"
            height={"56px"}
            borderRadius={"none"}
            onClick={handleAddOutfit}
            disabled={isLoading || !allSelected}
            loading={isLoading}
          >
            <Text textStyle={"bold16"}>
              {t('add_to_cart')}
            </Text>
          </Button>
        </Flex>
      </Flex>
    );
}
